import React, { memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

interface MarketplaceBannerProps {
  onPostItem: () => void;
  onMyProfile: () => void;
}

const BANNER_BUTTON_OUTLINE_BORDER = 'rgba(70,114,254,0.3)';

export const MarketplaceBanner: React.FC<MarketplaceBannerProps> = memo(({
  onPostItem,
  onMyProfile,
}) => {
  const { colors } = useThemeColors();
  return (
    <GlassCard padding={23} style={styles.card}>
      <View style={styles.row}>
        <View style={[styles.iconContainer, { backgroundColor: colors.accent.purple }]}>
          <RESIDENTS_SVG_COMPONENTS.SUPERMARKET
            width={22}
            height={22}
            color={PRIMITIVE_COLORS.resioPurple}
          />
        </View>

        <View style={styles.content}>
          <Text style={[styles.title, { color: colors.text.tertiary }]}>Community marketplace</Text>
          <Text style={[styles.description, { color: colors.text.dim }]}>
            Safe, local, and easy — right within your community.
          </Text>

          <View style={styles.buttons}>
            <Hoverable
              hover="button"
              style={styles.primaryButton}
              onPress={onPostItem}
            >
              <Text style={styles.primaryButtonText}>Post an item</Text>
            </Hoverable>

            <Hoverable
              hover="row"
              style={styles.outlineButton}
              onPress={onMyProfile}
            >
              <RESIDENTS_SVG_COMPONENTS.USER
                width={14}
                height={14}
                color={PRIMITIVE_COLORS.resioBlue}
              />
              <Text style={styles.outlineButtonText}>My profile</Text>
            </Hoverable>
          </View>
        </View>
      </View>
    </GlassCard>
  );
});

MarketplaceBanner.displayName = 'MarketplaceBanner';

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    flex: 1,
  },
  iconContainer: {
    width: 46,
    height: 46,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 4,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.small,
    lineHeight: 18.2,
  },
  buttons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
  },
  primaryButton: {
    flex: 1,
    maxWidth: 180,
    minHeight: 44,
    paddingVertical: 13, // 44 − ~16 line (13pt), split, 2pt slack
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    ...REDESIGN_TYPOGRAPHY.buttonSmall,
    color: '#ffffff',
  },
  outlineButton: {
    flex: 1,
    maxWidth: 180,
    minHeight: 44,
    paddingVertical: 12, // 44 − ~16 line − 2×1 border, split
    borderWidth: 1,
    borderColor: BANNER_BUTTON_OUTLINE_BORDER,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  outlineButtonText: {
    ...REDESIGN_TYPOGRAPHY.buttonSmall,
    color: PRIMITIVE_COLORS.resioBlue,
  },
});
